import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, OnInit } from '@angular/core';
import { combineLatest } from 'rxjs';

import { PageMetaService } from '@spartacus/core';

import { IHeading, OrientationEnum, PositionEnum, SdSpyTargetDirective, SizesEnum } from '@seb/web-design-system/core';
import { ITile, ITileModal, ITilesGroup, SdTilesGroupComponent } from '@seb/web-design-system/tiles-group';

import { CustomMedia, ExtendedIMedia, TilesComponentInterface, TilesModuleInterface } from '../../../models';
import { TilesModalLayoutEnum, TilesModuleLayoutEnum } from '../../../enums';
import { AbstractModuleComponent } from '../abstract-module.component';
import { WrapperComponent } from '../../../components/static/core';
import { ImageService } from '../../../services';
import { LinkMapper } from '../../../mappers';

@Component({
  selector: 'wc-tiles-module',
  templateUrl: './tiles-module.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [WrapperComponent, SdTilesGroupComponent, SdSpyTargetDirective]
})
export class TilesModuleComponent extends AbstractModuleComponent<TilesModuleInterface> implements OnInit {
  @Input() class = '';

  @HostBinding('class')
  get hostClasses(): string {
    return [this.class, 'wc-component is-cms'].filter(Boolean).join(' ');
  }

  sizesEnum = SizesEnum;
  titleLevelTilesGroup: number;
  layout: OrientationEnum;
  position: PositionEnum.left | PositionEnum.right;
  heading: IHeading;
  tilesGroup: ITilesGroup;
  context: string;

  constructor(
    private imageService: ImageService,
    private pageMetaService: PageMetaService,
    private cdRef: ChangeDetectorRef
  ) {
    super();
  }

  override mandatoryComponentFunction = (cmp: TilesComponentInterface): boolean => {
    return !!cmp.media || !!cmp.mediaContainer;
  };

  private static toModalLayout(layout: TilesModalLayoutEnum): OrientationEnum {
    switch (layout) {
      case TilesModalLayoutEnum.PORTRAIT:
        return OrientationEnum.portrait;
      case TilesModalLayoutEnum.LANDSCAPE:
        return OrientationEnum.landscape;
      default:
        return OrientationEnum.portrait;
    }
  }

  ngOnInit(): void {
    this.subscriptions.add(
      combineLatest([this.module$, this.pageMetaService.getMeta()]).subscribe(([module, pageMetaData]) => {
        this.setLayout(module);
        this.context = this.wrapper?.wrapperHeading?.headline
          ? `${pageMetaData.title} ${this.wrapper?.wrapperHeading?.headline}`
          : pageMetaData.title;
        this.tilesGroup = this.toTilesGroup(module);

        this.setLoaded();
        this.cdRef.markForCheck();
      })
    );
  }

  private setLayout(module: TilesModuleInterface): void {
    switch (module.layout) {
      case TilesModuleLayoutEnum.PORTRAIT_RIGHT:
        this.layout = OrientationEnum.portrait;
        this.position = PositionEnum.right;
        break;
      case TilesModuleLayoutEnum.LANDSCAPE_LEFT:
        this.layout = OrientationEnum.landscape;
        this.position = PositionEnum.left;
        break;
      case TilesModuleLayoutEnum.LANDSCAPE_RIGHT:
        this.layout = OrientationEnum.landscape;
        this.position = PositionEnum.right;
        break;
      default:
        this.layout = OrientationEnum.portrait;
        this.position = PositionEnum.left;
    }
  }

  private toTilesGroup(module: TilesModuleInterface): ITilesGroup {
    const tempITilesGroup: ITilesGroup = {
      tiles: []
    };
    module.componentsObjects.forEach((cmp) => {
      if (cmp.mediaContainer) {
        this.imageService
          .getMediaFromContainer(cmp.mediaContainer)
          .subscribe((customMedia: CustomMedia) =>
            tempITilesGroup.tiles.push(this.toTile(cmp, this.imageService.normalizeMedia(customMedia)))
          );
      } else {
        tempITilesGroup.tiles.push(this.toTile(cmp));
      }
    });
    return tempITilesGroup;
  }

  private toTile(component: TilesComponentInterface, mediaContainer?: ExtendedIMedia): ITile {
    if (mediaContainer) {
      return {
        title: component.headline,
        modal: this.toTileModal(component),
        ...mediaContainer
      };
    } else {
      return {
        title: component.headline,
        modal: this.toTileModal(component),
        ...this.imageService.normalizeMedia(component.media)
      };
    }
  }

  private toTileModal(component: TilesComponentInterface): ITileModal {
    let medias: CustomMedia[] = [];
    if (component.modalMedias) {
      medias = Object.values(component.modalMedias);
    }
    if (component.modalDescription && medias.length > 0) {
      return {
        layout: TilesModuleComponent.toModalLayout(component.modalLayout),
        title: component.modalHeadline,
        subtitle: component.modalSubline,
        description: component.modalDescription,
        link: LinkMapper.toLink(component.modalUrl, component.modalLinkName),
        medias: medias.map((media) => this.imageService.normalizeMedia(media))
      };
    }
    return undefined;
  }
}
